/**
 * Review-build JSX runtime.
 *
 * Next's dev transform calls `jsxDEV(type, props, key, isStatic, source)` where
 * `source` is `{ fileName, lineNumber, columnNumber }`. We forward everything to
 * React untouched, except that host elements (a lowercase `type`) also get a
 * `data-src="src/app/page.tsx:42:7"` attribute so a rendered pixel can be traced
 * back to the line that produced it.
 *
 * Enabled only via `jsxImportSource` in the review tsconfig — the live build
 * never loads this file.
 */
import { jsxDEV as reactJsxDEV } from "react/jsx-dev-runtime";

export { Fragment } from "react/jsx-dev-runtime";

/**
 * Re-export React's JSX namespace. TypeScript resolves JSX.IntrinsicElements
 * through whatever `jsxImportSource` points at, so without this every element
 * in the review build degrades to `any` with TS7026 and JSX typechecking is
 * silently switched off — which would make `tsc --noEmit` on the review
 * checkout report errors that do not exist in the real build.
 */
export type { JSX } from "react";

const ROOT = process.cwd() + "/";

/**
 * Turbopack reports `[project]/src/app/page.tsx`; Webpack/Node report an
 * absolute path. Normalise both to a repo-relative path so the anchor can be
 * fed straight to `read_file`.
 */
function rel(fileName: string): string {
  if (fileName.startsWith("[project]/")) return fileName.slice("[project]/".length);
  if (fileName.startsWith(ROOT)) return fileName.slice(ROOT.length);
  return fileName;
}

type Source = { fileName?: string; lineNumber?: number; columnNumber?: number };

export function jsxDEV(
  type: unknown,
  props: Record<string, unknown>,
  key: unknown,
  isStaticChildren: boolean,
  source: Source | undefined,
  self: unknown,
) {
  if (
    typeof type === "string" &&
    source?.fileName &&
    props &&
    props["data-src"] === undefined
  ) {
    props = {
      ...props,
      "data-src": `${rel(source.fileName)}:${source.lineNumber ?? 0}:${source.columnNumber ?? 0}`,
    };
  }
  // @ts-expect-error - react's dev runtime types are not public
  return reactJsxDEV(type, props, key, isStaticChildren, source, self);
}
